import styled, { css } from 'vue-styled-components';
import { applyHoverAndPress, square, COLORS } from '@styles/common.styled';

import {
    FLEX_CENTER_DIV,
    TOOLTIP_CONTENT,
} from '@styles/common.styled.component';

import CheckIcon from '@svgIcons/check.svg';
import QmarkIcon from '@svgIcons/qmark.svg';
import CloseIcon from '@svgIcons/close.svg';

export default {
    name: 'Checkbox',
    functional: true,
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        hasError: {
            type: Boolean,
            default: false,
        },
        isBlock: {
            type: Boolean,
            default: false,
        },
        tooltip: {
            type: String,
            default: '',
        },
        tooltipDirection: {
            type: String,
            default: 'bottom',
        },
        type: {
            type: String,
            default: 'big', //big | small
        },
        bgColor: {
            type: String,
            default: COLORS['blue-60'],
        },
        blockBgColor: {
            type: String,
            default: COLORS['gray-60'],
        },
        disableOnClick: {
            type: Boolean,
            default: false,
        },
        inactiveStyle: {
            type: Array,
        },
        hasBorder: {
            type: Boolean,
            default: false,
        },
        hasTooltip: {
            type: Boolean,
            default: true,
        },
        forceQmark: {
            type: Boolean,
            default: false,
        },
        indeterminate: {
            type: Boolean,
            default: false,
        },
    },
    render(h, data) {
        const { props, listeners = {}, slots = R.always({}) } = data;
        const { default: defaultSlot } = slots();
        const {
            value,
            hasError,
            bgColor,
            blockBgColor,
            isBlock,
            tooltip,
            disableOnClick,
            inactiveStyle,
            hasBorder,
            tooltipDirection,
            hasTooltip,
            forceQmark,
            indeterminate,
        } = props;
        const { input = R.identity } = listeners;

        let events = {};

        if (!disableOnClick) {
            events = {
                ...events,
                click: () => {
                    if (isBlock) {
                        return;
                    }
                    input(!value);
                },
            };
        }

        const content$ = (
            <div
                class="adriel-flex-vertical-center checkbox-wrapper"
                on={events}
            >
                <CheckboxArea
                    active={value}
                    indeterminate={indeterminate}
                    hasError={hasError}
                    bgColor={bgColor}
                    blockBgColor={blockBgColor}
                    inactiveStyle={inactiveStyle}
                    isBlock={isBlock}
                    hasBorder={hasBorder}
                    tooltip={tooltip || forceQmark}
                    disableOnClick={disableOnClick}
                >
                    {(tooltip || forceQmark) && (
                        <QmarkIcon
                            width="13px"
                            height="13px"
                            v-view-box
                            class="block qmark"
                            fill={COLORS['gray-30']}
                        />
                    )}
                    {isBlock && !tooltip && (
                        <CloseIcon v-view-box class="close" />
                    )}
                    {value && !isBlock && !indeterminate && (
                        <CheckIcon v-view-box class="check" />
                    )}
                    {indeterminate && !isBlock && <div class="bar" />}
                </CheckboxArea>

                {defaultSlot && (
                    <div class="adriel-flex-vertical-center checkbox-slot">
                        {defaultSlot}
                    </div>
                )}
            </div>
        );

        return hasTooltip ? (
            <a-tooltip placement={tooltipDirection} mouseLeaveDelay={0}>
                {content$}
                {tooltip && (
                    <TOOLTIP_CONTENT slot="title" size="small">
                        {tooltip}
                    </TOOLTIP_CONTENT>
                )}
            </a-tooltip>
        ) : (
            content$
        );
    },
};

const CheckboxArea = styled(FLEX_CENTER_DIV, {
    active: Boolean,
    indeterminate: Boolean,
    hasError: Boolean,
    bgColor: String,
    blockBgColor: String,
    isBlock: Boolean,
    inactiveStyle: Object | undefined,
    blockStyle: Object | undefined,
    hasBorder: Boolean,
    tooltip: String | Boolean,
    disableOnClick: Boolean,
})`
    ${square(16)};
    cursor: pointer;
    border-radius: 2px;
    transition: background-color 0.2s;

    & .block {
        margin-bottom: 0px !important;
    }

    & .bar {
        width: 8px;
        height: 1px;
        background: #fff;
    }

    & .close {
        fill: rgb(189, 194, 199);
    }

    ${({ isBlock, active, indeterminate, bgColor, tooltip }) => {
        if (isBlock) {
            return css`
                cursor: not-allowed;
                background-color: ${COLORS['gray-15']};
                border: ${tooltip ? `1px solid ${COLORS['gray-30']}` : 'none'};
                &:hover {
                    cursor: not-allowed;
                }
                & .check {
                    display: none;
                }
                & .bar {
                    display: none;
                }

                & .close {
                    display: ${tooltip ? 'none' : 'block'};
                }
            `;
        } else if (active || indeterminate) {
            return css`
                ${applyHoverAndPress(bgColor, 'background-color')}
                & .check {
                    transition: all 0.2s;
                    fill: white !important;
                    ${square(12)};
                    margin-right: unset;
                }
                & .block {
                    display: none;
                }

                & .bar {
                    display: ${indeterminate ? 'block' : 'none'};
                }

                & .close {
                    display: none;
                }
            `;
        } else if (!active) {
            return css`
                ${applyHoverAndPress(COLORS['gray-15'], 'background-color')}
                & .check {
                    display: none;
                }
                & .block {
                    display: none;
                }

                & .bar {
                    display: none;
                }

                & .close {
                    display: none;
                }
            `;
        }
    }};

    ${({ hasBorder }) =>
        hasBorder &&
        css`
            border: solid 1px ${COLORS['gray-25']};
        `}

    ${({ hasError }) =>
        hasError &&
        css`
            border: solid 1px #ff0000;
        `}
`;
